import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiExternalLink, FiMapPin, FiDollarSign, FiLoader, FiBriefcase, FiSearch, FiNavigation, FiPhone } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { aiService } from "../services";
import { toast } from "react-toastify";
import "../styles/components/_job-offers-modal.css";

const JobOffersModal = ({ isOpen, profession, onClose }) => {
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchJobOffers = useCallback(async () => {
    try {
      setLoading(true);
      setHasSearched(true);
      const response = await aiService.generateJobOffers(profession);
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error("Error fetching job offers:", error);
      toast.error(t("errors.fetchJobOffersError") || "Error al cargar las ofertas");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [profession, t]);

  useEffect(() => {
    if (isOpen && profession) {
      setHasSearched(false);
      fetchJobOffers();
    }
  }, [isOpen, profession, fetchJobOffers]);

  const handleOpenOffer = (url) => {
    if (url && url !== "#") {
      window.open(url, "_blank");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="job-offers-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {/* Modal */}
          <motion.div
            className="job-offers-modal"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="job-offers-header">
              <div className="job-offers-header-content">
                <div className="job-offers-header-icon">
                  <FiBriefcase size={28} />
                </div>
                <div>
                  <h2 className="job-offers-title">
                    {t("jobOffers.title")}
                  </h2>
                  <p className="job-offers-profession">{profession}</p>
                </div>
              </div>
              <button className="job-offers-close" onClick={onClose}>
                <FiX size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="job-offers-content">
              {loading ? (
                <motion.div
                  className="job-offers-loading"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <FiLoader size={40} />
                  </motion.div>
                  <p>{t("jobOffers.loading")}</p>
                  <span className="job-offers-loading-subtitle">{t("jobOffers.searching")}</span>
                </motion.div>
              ) : offers.length > 0 ? (
                <div className="job-offers-list">
                  {offers.map((offer, index) => (
                    <motion.div
                      key={index}
                      className="job-offer-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ translateY: -5 }}
                    >
                      {/* Offer Header */}
                      <div className="job-offer-header">
                        <div>
                          <h3 className="job-offer-title">{offer.title}</h3>
                          <p className="job-offer-company">{offer.company}</p>
                          {offer.source && (
                            <span className="job-offer-source">{offer.source}</span>
                          )}
                        </div>
                        <button
                          className="job-offer-open-btn"
                          onClick={() => handleOpenOffer(offer.url)}
                          title={t("jobOffers.visitOffer")}
                        >
                          <FiExternalLink size={18} />
                        </button>
                      </div>

                      {/* Offer Details */}
                      <div className="job-offer-details">
                        <div className="job-offer-detail">
                          <FiMapPin size={16} />
                          <span>{offer.location}</span>
                        </div>
                        {offer.address && (
                          <div className="job-offer-detail">
                            <FiNavigation size={16} />
                            <span>{offer.address}</span>
                          </div>
                        )}
                        {offer.contactNumber && (
                          <div className="job-offer-detail">
                            <FiPhone size={16} />
                            <span>{offer.contactNumber}</span>
                          </div>
                        )}
                        <div className="job-offer-detail">
                          <FiDollarSign size={16} />
                          <span>{offer.salary}</span>
                        </div>
                      </div>

                      {/* Offer Description */}
                      <p className="job-offer-description">{offer.description}</p>

                      {/* Action Button */}
                      <button
                        className="job-offer-apply-btn"
                        onClick={() => handleOpenOffer(offer.url)}
                      >
                        {t("jobOffers.applyNow")}
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : hasSearched ? (
                <motion.div
                  className="job-offers-empty"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <FiSearch size={48} className="job-offers-empty-icon" />
                  <h3 className="job-offers-empty-title">{t("jobOffers.noOffersTitle")}</h3>
                  <p className="job-offers-empty-text">{t("jobOffers.noOffersText")}</p>
                  <p className="job-offers-empty-suggestion">{t("jobOffers.noOffersSuggestion")}</p>
                  <button className="job-offers-retry-btn" onClick={fetchJobOffers}>
                    {t("jobOffers.tryAgain")}
                  </button>
                </motion.div>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JobOffersModal;
